import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  AddProduct,
  DownloadService,
  LicenseType,
  Product,
  ProductOffering,
  ProductService,
  UserDownloadInfo,
  UserService
} from '@uofu-uss-aux/software-catalog-ng-core';
import {Graphic, GraphicService} from '../../graphic/graphic.service';
import {Subscription} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {NotificationService} from '@uofu-uss/angular-util';
import {UserAccessService} from '../../user/user-access.service';
import {sortBy, sortedUniqBy} from 'lodash-es';
import {NgForOf, NgIf} from '@angular/common';
import {LicenseInfoComponent} from '../../product/license-info/license-info.component';
import {LicenseExpirationWarningComponent} from '../../product/license-expiration-warning/license-expiration-warning.component';
import {DownloadWithoutPurchaseItemComponent} from '../download-without-purchase-item/download-without-purchase-item.component';
import {CatalogGraphicComponent} from '../../graphic/catalog-graphic/catalog-graphic.component';

export type DownloadWithOffering = {
  offering: ProductOffering;
  downloads: ReadonlyArray<UserDownloadInfo>;
};

@Component({
  selector: 'app-downloads-without-purchase-details',
  templateUrl: './download-without-purchase-details.component.html',
  styleUrls: ['./download-without-purchase-details.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    LicenseInfoComponent,
    LicenseExpirationWarningComponent,
    NgForOf,
    DownloadWithoutPurchaseItemComponent,
    CatalogGraphicComponent
  ]
})
export class DownloadWithoutPurchaseDetailsComponent extends AddProduct(Object) implements OnInit, OnDestroy {

  constructor(private readonly route: ActivatedRoute,
              private readonly productService: ProductService,
              private readonly notificationService: NotificationService,
              private readonly graphicService: GraphicService,
              private readonly userService: UserService,
              private readonly downloadService: DownloadService,
              private readonly userAccessService: UserAccessService) {
    super();
  }

  get hasOfferings(): boolean {
    return this._hasOfferings;
  }

  static failedToLoadMessage = 'Unable to load downloads';

  graphic: Graphic;
  offerings: ReadonlyArray<ProductOffering> = [];
  downloadsItems: Array<DownloadWithOffering> = [];
  version = '';

  private subscription: Subscription;
  private _hasOfferings = false;

  ngOnInit(): void {
    this.subscription = this.route.paramMap.subscribe(pm => {
      const id: number = +pm.get('product-id');
      this.version = pm.get('offering-version');
      const licenseTypes = this.userAccessService.determineAllowedLicenseTypes(this.userService.value);

      this.initializeWithProductAndVersion(id, this.version, licenseTypes);
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  initializeWithProductAndVersion(id: number, version: string, allowedLicenses: ReadonlyArray<LicenseType>): Subscription {
    const observer = {
      next: (product: Product) => {
        this.product = product;
        this.initializedGraphic(product.imageId);
        this.offerings = product.licenses
          .filter(l => allowedLicenses.find(al => al === l.type))
          .flatMap(l => l.offerings)
          .filter(off => off.downloadAvailableWithoutPurchase && off.version === version);
        this._hasOfferings = !!this.offerings.length;
        this.initializeDownloads(this.offerings);
      },
      error: () => this.notificationService.addError(DownloadWithoutPurchaseDetailsComponent.failedToLoadMessage),
    };
    return this.productService.fetchProduct(id)
      .pipe(first())
      .subscribe(observer);
  }

  private initializedGraphic(id: number): void {
    this.graphicService.fetchGraphic(id)
      .pipe(first())
      .subscribe(g => this.graphic = g);
  }

  private findByPlatform(downloadWithOffering: DownloadWithOffering): DownloadWithOffering {
    return this.downloadsItems.find(downloadItem => {
      return downloadItem.offering.platform.id === downloadWithOffering.offering.platform.id;
    });
  }

  private initializeDownloads(offerings: ReadonlyArray<ProductOffering>): void {
    const observer = {
      next: (downloadsWithOffering: DownloadWithOffering) => {
        /*
         Check to see if we already have the platform before adding a new entry. If we do then append new items
         making sure that they are distinct by name and sorted by name.
         */
        const existing = this.findByPlatform(downloadsWithOffering);
        const offering: ProductOffering = downloadsWithOffering.offering;
        if (!existing) {
          console.debug(`Adding download for offering ${offering.id} ${offering.version} ${offering.platform.title}`);
          this.downloadsItems.push(downloadsWithOffering);
        } else {
          console.debug(`Updating downloads for offering ${offering.id} ${offering.version} ${offering.platform.title}`);
          existing.downloads = sortedUniqBy(sortBy(existing.downloads.concat(downloadsWithOffering.downloads),
            (dl) => dl.name), (dl) => dl.name);
        }
      },
      error: () => this.notificationService.addError(DownloadWithoutPurchaseDetailsComponent.failedToLoadMessage)
    };

    offerings.forEach(offering => {
      this.downloadService.fetchDownloadsByOffering(offering.id)
        .pipe(first(),
          map(downloads => sortBy(downloads || [], (dl) => dl.name)),
          map(downloads => ({offering, downloads})))
        .subscribe(observer);
    });
  }
}
